import { Feature, Polygon } from '@turf/turf';

/**
 * Where a suggested field boundary came from. Drives the notice shown on the
 * field creation screen, since a boundary derived from the coverage is less
 * reliable than the one recorded on the device.
 */
export enum FieldBoundarySuggestionSource {
  DEVICE_BOUNDARY = 'device-boundary',
  COVERAGE = 'coverage',
}

export interface LatLngPoint {
  lat: number;
  lng: number;
}

/** Boundary recorded on the device, as produced by the task parsers (`evento.boundaries`). */
export interface DeviceBoundary {
  path: LatLngPoint[];
}

/** The parts of a processed task the suggestion is built from. */
export interface FieldBoundarySuggestionTask {
  boundaries?: DeviceBoundary[] | null;
  trails?: unknown;
  /** Tells transit corridors apart from the field; see `implementWidthFromSteps`. */
  implementWidthMeters?: number | null;
}

/** The same task with its trails already reduced to the worked polygons. */
export interface FieldBoundaryCoverageTask {
  boundaries?: DeviceBoundary[] | null;
  coveragePolygons: LatLngPoint[][];
  implementWidthMeters?: number | null;
}

/** NVG task step (`evento.etapa`) fields that describe the implement. */
export interface ImplementStep {
  impl_width?: number | string | null;
  sec_width_meters?: unknown;
}

/**
 * ISOBUS task stage (`evento.stageInfo`). Its `ecusData` is keyed by device,
 * each entry holding `implementWidth` as `[value, unit]`.
 */
export interface IsobusStage {
  ecusData?: unknown;
}

export interface FieldBoundarySuggestion {
  source: FieldBoundarySuggestionSource;
  feature: Feature<Polygon>;
}

export interface FieldBoundarySuggestionOptions {
  /** Raster resolution; also the precision of a coverage-derived boundary. */
  cellSizeMeters: number;
  /** Unworked strips narrower than about twice this width are closed. */
  gapClosingMeters: number;
  /** Raster size limit; cells grow beyond `cellSizeMeters` when a task spans a larger area. */
  maxGridCells: number;
  /** Suggestions smaller than this are discarded as noise. */
  minAreaSquareMeters: number;
  /**
   * Largest distance the simplified outline may stray from the traced one.
   * Trades vertices for precision; never below the cell size.
   */
  simplifyToleranceMeters: number;
  /**
   * Worked blocks up to this far apart are taken as the same field worked in
   * strips and joined into one boundary; farther blocks are left out as other
   * fields. Zero keeps only the largest block.
   */
  maxJoinGapMeters: number;
}

export const DEFAULT_FIELD_BOUNDARY_SUGGESTION_OPTIONS: FieldBoundarySuggestionOptions =
  {
    cellSizeMeters: 5,
    gapClosingMeters: 10,
    maxGridCells: 1000000,
    minAreaSquareMeters: 1000,
    simplifyToleranceMeters: 10,
    maxJoinGapMeters: 400,
  };
